import { useQuery } from "@tanstack/react-query"
import { AlertCircle, FileText, Plus } from "lucide-react"
import { useRef, useState } from "react"
import { Link } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { DialogoNuevaCotizacion } from "@/componentes/finanzas/DialogoNuevaCotizacion"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * Bandeja de cotizaciones.
 *
 * La paginación es **del servidor**: se piden `limit`/`offset` y se pinta lo que
 * llega. Traerse la tabla entera y cortarla aquí se ve idéntico con los datos de
 * prueba y muere el primer día de producción.
 */

type EstadoCotizacion = "BORRADOR" | "ENVIADA" | "APROBADA" | "RECHAZADA"

interface FilaCotizacion {
  id: number
  numero: string
  cliente_id: number
  aduana_id: number
  estado: EstadoCotizacion
  tasa_cambio: string
  creado_en: string
}

interface Pagina {
  total: number
  limit: number
  offset: number
  items: FilaCotizacion[]
}

const POR_PAGINA = 25

/** Un color por estado, tomado de las variables del tema. Ningún hex aquí. */
const COLOR_ESTADO: Record<EstadoCotizacion, string> = {
  BORRADOR: "var(--fg-muted)",
  ENVIADA: "var(--info)",
  APROBADA: "var(--success)",
  RECHAZADA: "var(--destructive)",
}

function Etiqueta({ estado }: { estado: EstadoCotizacion }) {
  return (
    <span
      className="inline-block rounded-[var(--radius-control)] border px-2 py-0.5 text-xs font-medium"
      style={{ color: COLOR_ESTADO[estado], borderColor: "var(--border)" }}
    >
      {estado}
    </span>
  )
}

export function Cotizaciones() {
  const [offset, setOffset] = useState(0)
  const [estado, setEstado] = useState<EstadoCotizacion | "">("")
  const [creando, setCreando] = useState(false)
  const usuario = usarSesion((e) => e.usuario)

  const parametros = new URLSearchParams({ limit: String(POR_PAGINA), offset: String(offset) })
  if (estado) parametros.set("estado", estado)

  const consulta = useQuery({
    // Clave estable: la tupla incluye TODO lo que cambia la respuesta. Si el
    // filtro faltara aquí, cambiarlo mostraría la página anterior cacheada.
    queryKey: ["cotizaciones", { offset, estado }],
    queryFn: () => api.get<Pagina>(`/api/v1/cotizaciones?${parametros}`),
  })

  const pagina = consulta.data
  const hasta = pagina ? Math.min(pagina.offset + pagina.limit, pagina.total) : 0
  const vista = useRef<HTMLDivElement | null>(null)
  const subtitulo = [
    estado ? `Estado: ${estado}` : "Todos los estados",
    pagina && pagina.total > 0 ? `${pagina.offset + 1}–${hasta} de ${pagina.total}` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        {/* Vuelve a ser una entrada del menú —«Comercial» es una SECCIÓN, no una
            pantalla—, así que vuelve a poner su título. */}
        <div>
          <h1 className="text-xl font-semibold">Cotizaciones</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {pagina ? `${pagina.total} en total` : " "}
          </p>
        </div>

        {/* Esconder el botón es COSMÉTICO: el servidor decide igual. Sirve para
            no ofrecer algo que va a responder 403. */}
        <div className="flex flex-wrap items-start gap-2">
          <Exportar titulo="Cotizaciones" subtitulo={subtitulo} contenido={vista} />
          {tienePermiso(usuario, "cotizacion:crear") && (
            <button
              type="button"
              onClick={() => setCreando((abierto) => !abierto)}
              aria-expanded={creando}
              className="flex h-9 items-center gap-2 rounded-[var(--radius-control)] px-3 text-sm font-medium"
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
            >
              <Plus size={16} aria-hidden="true" />
              Nueva
            </button>
          )}
        </div>
      </div>

      {/*
        ⚠ El wizard va ARRIBA de la bandeja, no en una ruta aparte. Crear una
        cotización es mirar las que ya existen mientras se teclea la nueva, y sacarlo
        a otra pantalla obliga a volver para comprobar si esto ya se cotizó.
      */}
      {creando && (
        <div className="mb-6 superficie p-4">
          <DialogoNuevaCotizacion onCerrar={() => setCreando(false)} />
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="filtro-estado" className="mr-2 text-sm">
          Estado
        </label>
        <select
          id="filtro-estado"
          value={estado}
          onChange={(e) => {
            setEstado(e.target.value as EstadoCotizacion | "")
            // Cambiar el filtro vuelve a la primera página: quedarse en el
            // desplazamiento anterior deja al usuario mirando una tabla vacía.
            setOffset(0)
          }}
          className="h-9 rounded-[var(--radius-control)] border px-2 text-sm"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--surface)",
            color: "var(--fg)",
          }}
        >
          <option value="">Todos</option>
          <option value="BORRADOR">Borrador</option>
          <option value="ENVIADA">Enviada</option>
          <option value="APROBADA">Aprobada</option>
          <option value="RECHAZADA">Rechazada</option>
        </select>
      </div>

      {consulta.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando cotizaciones…
        </p>
      )}

      {consulta.isError && (
        <div
          role="alert"
          className="flex items-start gap-3 superficie p-4"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertCircle size={20} aria-hidden="true" />
          <div>
            <p className="font-medium">No se pudieron cargar las cotizaciones</p>
            <p className="mt-1 text-sm">
              {consulta.error instanceof ErrorApi
                ? consulta.error.message
                : "Error inesperado de red"}
            </p>
          </div>
        </div>
      )}

      {pagina && pagina.items.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <FileText size={24} aria-hidden="true" />
          <p className="text-sm">
            {estado ? `No hay cotizaciones en ${estado}` : "Todavía no hay cotizaciones"}
          </p>
        </div>
      )}

      {pagina && pagina.items.length > 0 && (
        <div ref={vista}>
          <Tabla etiqueta="Cotizaciones">
            <Cabecera>
              <Th>Número</Th>
              <Th>Estado</Th>
              <Th derecha>Tasa</Th>
              <Th derecha>Creada</Th>
            </Cabecera>
            <Cuerpo>
              {pagina.items.map((fila) => (
                <Fila key={fila.id}>
                  <Td>
                    <Link to={`/cotizaciones/${fila.id}`} className="font-medium underline">
                      {fila.numero}
                    </Link>
                  </Td>
                  <Td>
                    <Etiqueta estado={fila.estado} />
                  </Td>
                  {/* `tabular` en toda celda con cifras o fechas: sin eso los
                      dígitos bailan al paginar y la tabla parece temblar. */}
                  <Td derecha numero data-exportar-valor={fila.tasa_cambio.replace(".", ",")}>
                    {fila.tasa_cambio}
                  </Td>
                  <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                    {comoFecha(fila.creado_en)}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        </div>
      )}

      {pagina && pagina.total > pagina.limit && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="tabular" style={{ color: "var(--fg-muted)" }}>
            {pagina.offset + 1}–{hasta} de {pagina.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - POR_PAGINA))}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={hasta >= pagina.total}
              onClick={() => setOffset(offset + POR_PAGINA)}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
